import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { ArrowLeft, Copy, Download, Printer, Loader2, CheckCircle2, Circle, AlertTriangle, ClipboardList } from "lucide-react";

const NC = "Not configured yet";

const FIELDS = [
  ["business_name", "Business name"],
  ["brand_name", "Brand name (territory)"],
  ["territory_name", "Territory / franchise"],
  ["city", "City"],
  ["state_province", "State / province"],
  ["country", "Country"],
  ["public_phone", "Public phone"],
  ["public_email", "Public email"],
  ["public_website_url", "Website URL"],
  ["local_landing_page_url", "Local landing page URL"],
  ["consultation_booking_url", "Consultation booking URL"],
  ["service_area", "Service area"],
  ["covered_areas", "Covered areas"],
  ["primary_service_categories", "Primary service categories"],
  ["service_lanes_offered", "Service lanes offered"],
  ["short_description", "Short business description"],
  ["long_description", "Long business description"],
  ["public_team_summary", "Public team summary"],
  ["public_partner_summary", "Public partner / facility highlights"],
  ["local_faq_highlights", "Local FAQ highlights"],
  ["local_resources", "Local resources"],
];

const SICON = {
  ok: { I: CheckCircle2, c: "#2F7D5A" },
  warn: { I: AlertTriangle, c: "#B7791F" },
  todo: { I: Circle, c: "#9AA8B2" },
};

function valToText(v) {
  if (v == null || v === NC) return NC;
  if (typeof v === "string") return v;
  if (Array.isArray(v)) {
    if (v.length === 0) return NC;
    return v
      .map((it) =>
        typeof it === "string"
          ? it
          : it.q
          ? `Q: ${it.q}\nA: ${it.a}`
          : it.label || it.url
          ? `${it.label || ""}${it.url ? ` — ${it.url}` : ""}`.trim()
          : JSON.stringify(it)
      )
      .join("\n");
  }
  return String(v);
}

function valToCsv(v) {
  return valToText(v).replace(/\r?\n/g, " • ").trim();
}

function renderValue(v) {
  if (v == null || v === NC || (Array.isArray(v) && v.length === 0))
    return <span className="italic text-[#9AA8B2]">{NC}</span>;
  if (typeof v === "string") {
    if (/^https?:\/\//.test(v))
      return (
        <a href={v} target="_blank" rel="noreferrer" className="text-[#1C4358] underline hover:text-[#9C1E22] break-all">
          {v}
        </a>
      );
    return <span className="whitespace-pre-line">{v}</span>;
  }
  if (Array.isArray(v)) {
    return (
      <ul className="space-y-1 list-disc pl-4">
        {v.map((it, i) => (
          <li key={i}>
            {typeof it === "string" ? (
              it
            ) : it.q ? (
              <span>
                <b>{it.q}</b> — {it.a}
              </span>
            ) : it.label || it.url ? (
              <a href={it.url} target="_blank" rel="noreferrer" className="underline text-[#1C4358] hover:text-[#9C1E22] break-all">
                {it.label || it.url}
              </a>
            ) : (
              JSON.stringify(it)
            )}
          </li>
        ))}
      </ul>
    );
  }
  return String(v);
}

const GBP_NOTE =
  "MovingMyMama helps families plan senior transitions for a parent, spouse, or loved one. Service areas and availability may vary by territory.";

function blockVal(v, sep = ", ") {
  if (v == null || v === NC) return NC;
  if (typeof v === "string") return v;
  if (Array.isArray(v)) {
    if (v.length === 0) return NC;
    if (v[0] && typeof v[0] === "object" && v[0].q)
      return "\n" + v.map((f) => `  - ${f.q}\n    ${f.a}`).join("\n");
    if (v[0] && typeof v[0] === "object")
      return v.map((it) => (it.label ? `${it.label}${it.url ? ` (${it.url})` : ""}` : JSON.stringify(it))).join(sep);
    return v.join(sep);
  }
  return String(v);
}

function buildGbpBlock(p) {
  const areas = Array.isArray(p.covered_areas)
    ? p.covered_areas.join(", ")
    : p.service_area && p.service_area !== NC
    ? p.service_area
    : NC;
  const primary =
    Array.isArray(p.primary_service_categories) && p.primary_service_categories.length
      ? p.primary_service_categories[0]
      : NC;
  return [
    `Business Name:\n${p.brand_name}`,
    `Primary Category:\n${primary}`,
    `Service Categories:\n${blockVal(p.service_lanes_offered)}`,
    `Business Description:\n${blockVal(p.short_description)}`,
    `Long Description:\n${blockVal(p.long_description)}`,
    `Service Area:\n${areas}`,
    `Website:\n${blockVal(p.local_landing_page_url)}`,
    `Booking URL:\n${blockVal(p.consultation_booking_url)}`,
    `Phone:\n${blockVal(p.public_phone)}`,
    `Email:\n${blockVal(p.public_email)}`,
    `Services:\n${blockVal(p.service_lanes_offered)}`,
    `Local Team Summary:\n${blockVal(p.public_team_summary, "; ")}`,
    `Partner / Facility Highlights:\n${blockVal(p.public_partner_summary, "; ")}`,
    `FAQ Highlights:\n${blockVal(p.local_faq_highlights)}`,
    `Notes:\n${GBP_NOTE}`,
  ].join("\n\n");
}

export default function LocalProfileExport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/franchise-admin/franchises/${id}/local-profile-export`);
      setD(data);
    } catch (e) {
      toast.error(errMsg(e, "Could not load the local profile export."));
      if (e?.response?.status === 403) navigate("/franchise-admin");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const copy = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied`);
    } catch {
      toast.error("Copy failed — select and copy manually.");
    }
  };

  if (loading || !d) return <div className="flex justify-center py-24"><Loader2 className="animate-spin text-[#1C4358]" size={30} /></div>;

  const p = d.profile;
  const allText = FIELDS.map(([k, l]) => `${l}:\n${valToText(p[k])}`).join("\n\n");

  const downloadCsv = () => {
    const esc = (s) => `"${String(s).replace(/"/g, '""')}"`;
    const rows = [["Field", "Value"], ...FIELDS.map(([k, l]) => [l, valToCsv(p[k])])];
    rows.push(["Schema markup", d.schema_status.map((s) => `${s.label}=${s.status}`).join(" | ")]);
    rows.push(["SEO checklist", `${d.seo_checklist.readiness.ok}/${d.seo_checklist.readiness.total} setup complete`]);
    const csv = rows.map((r) => r.map(esc).join(",")).join("\r\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `local-profile-${(d.franchise.name || "territory").replace(/\s+/g, "-").toLowerCase()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="max-w-4xl mx-auto pb-24" data-testid="local-profile-export-page">
      <div className="flex items-center justify-between gap-3 flex-wrap print:hidden">
        <div className="flex items-center gap-2 min-w-0">
          <button onClick={() => navigate(`/franchise-admin/${id}/content`)} data-testid="lpe-back" className="text-[#1C4358] hover:text-[#9C1E22]">
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-lg font-display font-bold text-[#1C4358] truncate">{d.franchise.name} — Local Profile Export</h1>
        </div>
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={() => copy(buildGbpBlock(p), "Google Business Profile block")} data-testid="lpe-copy-gbp" className="gap-1.5 bg-[#1C4358] hover:bg-[#15303f] text-white">
            <ClipboardList size={14} /> Copy as Google Business Profile Block
          </Button>
          <Button size="sm" variant="outline" onClick={() => copy(allText, "All profile info")} data-testid="lpe-copy-all" className="gap-1.5 border-[#D6E0E7] text-[#1C4358]">
            <Copy size={14} /> Copy all
          </Button>
          <Button size="sm" variant="outline" onClick={downloadCsv} data-testid="lpe-download-csv" className="gap-1.5 border-[#D6E0E7] text-[#1C4358]">
            <Download size={14} /> CSV
          </Button>
          <Button size="sm" variant="outline" onClick={() => window.print()} data-testid="lpe-print" className="gap-1.5 border-[#D6E0E7] text-[#1C4358]">
            <Printer size={14} /> Print
          </Button>
        </div>
      </div>
      <p className="mt-2 text-[13px] text-[#4B5563] print:hidden">
        Copy these public-ready details into Google Business Profile or a local directory listing. Fields marked
        “{NC}” aren't set yet — configure them in Local Content; nothing is invented.
      </p>

      <Card className="mt-5 p-5 border-[#E6EDF2] divide-y divide-[#EEF3F6]" data-testid="lpe-fields">
        {FIELDS.map(([k, l]) => {
          const v = p[k];
          const empty = v == null || v === NC || (Array.isArray(v) && v.length === 0);
          return (
            <div key={k} className="py-3 flex items-start gap-3" data-testid={`lpe-field-${k}`}>
              <div className="w-44 shrink-0 text-[13px] font-semibold text-[#7A8A99] pt-0.5">{l}</div>
              <div className="min-w-0 flex-1 text-[14px] text-[#1C4358]">{renderValue(v)}</div>
              {!empty && (
                <button onClick={() => copy(valToText(v), l)} data-testid={`lpe-copy-${k}`} className="print:hidden text-[#7A8A99] hover:text-[#1C4358] shrink-0" title={`Copy ${l}`}>
                  <Copy size={14} />
                </button>
              )}
            </div>
          );
        })}
      </Card>

      <Card className="mt-5 p-5 border-[#E6EDF2]" data-testid="lpe-readiness">
        <h2 className="font-display font-semibold text-[#1C4358] text-[15px]">Listing readiness</h2>
        <div className="mt-3 grid gap-4 sm:grid-cols-2">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wide text-[#7A8A99] mb-1">Schema markup</p>
            {d.schema_status.map((s) => {
              const ic = SICON[s.status] || SICON.todo;
              const I = ic.I;
              return (
                <div key={s.key} className="flex items-center gap-2 py-1 text-[13.5px] text-[#1C4358]" data-testid={`lpe-schema-${s.key}`}>
                  <I size={15} style={{ color: ic.c }} /> {s.label}
                </div>
              );
            })}
          </div>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wide text-[#7A8A99] mb-1">
              SEO checklist — {d.seo_checklist.readiness.ok}/{d.seo_checklist.readiness.total} setup complete
            </p>
            {d.seo_checklist.items.map((s) => {
              const ic = SICON[s.status] || SICON.todo;
              const I = ic.I;
              return (
                <div key={s.key} className="flex items-center gap-2 py-0.5 text-[13px] text-[#4B5563]" data-testid={`lpe-seo-${s.key}`}>
                  <I size={14} style={{ color: ic.c }} /> {s.label}
                </div>
              );
            })}
          </div>
        </div>
      </Card>
    </div>
  );
}
